import { useCallback, useMemo } from 'react';

import * as events from 'src/lib/events';
import { SUGGESTED_AUDIENCES } from 'src/lib/events';
import * as gtm from 'src/lib/gtm';
import * as storage from 'src/lib/storage';
import { useTrackingEvent } from 'src/services/useTrackingEvent';
import { useUiActions } from 'src/store';

import { useGetProjectAttributes } from '../../services';

import { useBriefUpdateEvent } from './services';
import { useGetDocumentCustomDimensions, usePrepareGeneratedUsing } from './services';
import { useLocalActions } from './store';

export default function useEvents() {
  const localActions = useLocalActions();
  const uiActions = useUiActions();
  const getProjectAttributes = useGetProjectAttributes();
  const getDocumentCustomDimensions = useGetDocumentCustomDimensions();
  const getGeneratedUsing = usePrepareGeneratedUsing();
  const trackingEvent = useTrackingEvent();
  const briefUpdateEvent = useBriefUpdateEvent();

  const trackRecompose = useCallback(
    (props) => {
      const generationTool = localActions.getGenerationTool();

      // 1st event
      gtm.pushDataLayerEvent(gtm.EVENTS.aiQueryMade);

      // 2nd event
      const name = `${generationTool}${gtm.EVENTS.assetQueryMadePostfix}`;
      gtm.pushDataLayerEvent(name);

      // 3rd event
      gtm.pushDataLayerEvent(gtm.EVENTS.rephraseQueryMade);

      if (uiActions.wasWelcomePopupShown()) {
        if (!storage.readItem('firstCopyGenerationDone')) {
          storage.writeItem('firstCopyGenerationDone', true);
          gtm.pushDataLayerEvent(gtm.EVENTS.firstCopyGeneration);
        }
      }

      trackingEvent(events.DOCUMENT.clickedGenerateRecompose, {
        ...getProjectAttributes(),
        ...getDocumentCustomDimensions(props),
        generatedUsing: getGeneratedUsing(),
        page: events.EVENT_PAGE.editor,
        subPage: events.EVENT_SUB_PAGE.generationPanel,
      });
    },
    [
      localActions,
      uiActions,
      trackingEvent,
      getProjectAttributes,
      getDocumentCustomDimensions,
      getGeneratedUsing,
    ]
  );

  const trackSuggest = useCallback(
    (props) => {
      const generationTool = localActions.getGenerationTool();
      // 1st event
      gtm.pushDataLayerEvent(gtm.EVENTS.aiQueryMade);

      // 2nd event
      const name = `${generationTool}${gtm.EVENTS.assetQueryMadePostfix}`;
      gtm.pushDataLayerEvent(name);

      if (uiActions.wasWelcomePopupShown()) {
        if (!storage.readItem('firstCopyGenerationDone')) {
          storage.writeItem('firstCopyGenerationDone', true);
          gtm.pushDataLayerEvent(gtm.EVENTS.firstCopyGeneration);
        }
      }

      trackingEvent(events.DOCUMENT.clickedGenerateSuggest, {
        ...getProjectAttributes(),
        ...getDocumentCustomDimensions(props),
        page: events.EVENT_PAGE.editor,
        subPage: events.EVENT_SUB_PAGE.generationPanel,
      });

      if (props?.personaForGeneration?.isSuggested) {
        trackingEvent(SUGGESTED_AUDIENCES.generation, {
          ...getDocumentCustomDimensions([]),
          page: events.EVENT_PAGE.targetAudience,
        });
      }
    },
    [localActions, uiActions, trackingEvent, getProjectAttributes, getDocumentCustomDimensions]
  );

  const trackGenerateWithSuggestedPrompt = useCallback(
    (type) => {
      trackingEvent(events.DOCUMENT.freestyleGenerateWithSuggestedPrompt, {
        ...getProjectAttributes(),
        ...getDocumentCustomDimensions([]),
        page: events.EVENT_PAGE.editor,
        type,
      });
    },
    [trackingEvent, getProjectAttributes, getDocumentCustomDimensions]
  );

  const trackGenerateWithCallToAction = useCallback(
    (generationTool) => {
      const dimensions = {
        generationTool,
      };

      trackingEvent(events.DOCUMENT.generateWithCallToAction, {
        ...getProjectAttributes(),
        ...dimensions,
      });
    },
    [trackingEvent, getProjectAttributes]
  );

  const trackMultiPromptSetup = useCallback(
    (props) => {
      trackingEvent(events.DOCUMENT.multiPromptSetup, {
        ...getProjectAttributes(),
        ...getDocumentCustomDimensions(props),
        page: events.EVENT_PAGE.editor,
        subPage: events.EVENT_SUB_PAGE.generationPanel,
      });
    },
    [trackingEvent, getProjectAttributes, getDocumentCustomDimensions]
  );

  const trackVariationZeroResult = useCallback(
    (result) => {
      trackingEvent(events.DOCUMENT.variationZeroResult, {
        page: events.EVENT_PAGE.editor,
        result,
        subPage: events.EVENT_SUB_PAGE.listView,
      });
    },
    [trackingEvent]
  );

  return useMemo(
    () => ({
      trackSuggest,
      trackRecompose,
      trackGenerateWithSuggestedPrompt,
      trackGenerateWithCallToAction,
      trackMultiPromptSetup,
      trackVariationZeroResult,
      ...briefUpdateEvent,
    }),
    [
      trackSuggest,
      trackRecompose,
      trackGenerateWithSuggestedPrompt,
      trackGenerateWithCallToAction,
      trackMultiPromptSetup,
      trackVariationZeroResult,
      briefUpdateEvent,
    ]
  );
}
